import React, {
  forwardRef,
  Ref,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import moment from 'moment-timezone';
import ReactSelect, {
  Props as ReactSelectProps,
  ActionMeta,
  SelectComponentsConfig,
  SingleValueProps,
} from 'react-select';
import classnames from 'classnames';

import { useTrackReactSelect } from './useReactSelectChangeLog';
import {
  OptionWithSecondaryText,
  SingleValueWithSecondaryText,
} from './OptionWithSecondaryText';
import { OptionProps } from 'react-select';
import { useFormikContext } from 'formik';

import { useReactSelectStyle } from './Select';
import { GenericInput, GenericInputProps } from '../GenericInput';
import { useTheme } from 'react-jss';
import { Theme } from '@app/theme';

interface TimezoneOption {
  label: string;
  currentHour: string;
  value: string;
  HM: string;
}

interface Props {
  label?: string | React.ReactNode;
  name?: string;
  noMargin?: boolean;
  noBottomMargin?: boolean;
  rightAction?: React.ReactNode;
  onChange?: (value: string, actionMeta: ActionMeta<TimezoneOption>) => void;
  value?: string;
}

type InnerSelectProps = Partial<GenericInputProps> & ReactSelectProps;

const emptyFunc = () => {};
const components: SelectComponentsConfig<TimezoneOption, false> = {
  Option: ({ innerProps, ...props }: OptionProps<TimezoneOption, false>) => {
    const _innerProps = useMemo(() => {
      return {
        ...innerProps,
        onMouseMove: emptyFunc,
        onMouseOver: emptyFunc,
      };
    }, [innerProps]);
    return (
      <OptionWithSecondaryText
        {...props}
        innerProps={_innerProps}
        secondaryLabelKeys="currentHour"
      />
    );
  },
  SingleValue: (props: SingleValueProps<any>) => (
    <SingleValueWithSecondaryText {...props} secondaryLabelKeys="currentHour" />
  ),
};

const InnerComponent = memo(
  ({ className, trackingName, ...props }: InnerSelectProps) => {
    const theme: Theme = useTheme();
    const classes: any = useReactSelectStyle({ ...props, theme });
    const onChange = useTrackReactSelect<TimezoneOption, false>(
      trackingName,
      props.onChange,
    );

    const Select = ReactSelect as any; // @TODO update react-select
    return (
      <label>
        <Select
          {...props}
          classNamePrefix="react-select"
          className={classnames(classes.picker, {
            small: props.small,
          })}
          onChange={onChange}
          components={components}
        />
      </label>
    );
  },
);

type SelectProps = Props & ReactSelectProps<any>;

export const TimezoneSelect = forwardRef(
  (
    { onChange, isMulti, value, name, ...props }: SelectProps,
    ref: Ref<any>,
  ) => {
    const [HM, setHM] = useState(moment().format('HM'));

    const formikContext = useFormikContext<any>();
    const isUsingFormik = !!formikContext && !!name;
    const setFieldValue = formikContext && formikContext.setFieldValue;

    useEffect(() => {
      const interval = setInterval(() => {
        setHM(moment().format('LT'));
      }, 1000);

      return () => clearInterval(interval);
    }, [setHM]);

    const tzOptions = useMemo(() => {
      const now = new Date();
      const nowTimestamp = now.getUTCMilliseconds() / 1000;
      return moment.tz.names().map((tzName: string) => {
        const zone = moment.tz.zone(tzName);
        if (!zone) {
          return { label: tzName, value: tzName };
        }
        return {
          label: `${`${tzName}`.replace('_', ' ')} (${zone.abbr(
            nowTimestamp,
          )})`,
          currentHour: moment.tz(now, tzName).format('HH:mm'),
          value: tzName,
          HM,
        };
      });
    }, [HM]);

    const _onChange = useCallback(
      (option: any, action: ActionMeta<TimezoneOption>) => {
        if (onChange) {
          onChange(option.value, action);
        } else if (setFieldValue && name) {
          setFieldValue(name, option.value);
        }
      },
      [onChange, name, setFieldValue],
    );

    const formikValue =
      formikContext && name && formikContext.getFieldMeta(name).value;

    const val = useMemo(() => {
      const actialValue = !value && isUsingFormik ? formikValue : value;
      return tzOptions.find((option) => option.value === actialValue);
    }, [value, tzOptions, isUsingFormik, formikValue]);

    return (
      <React.Fragment>
        <GenericInput
          component={InnerComponent}
          {...props}
          ref={ref}
          onChange={_onChange}
          value={val}
          options={tzOptions}
          isMulti={isMulti}
          disableLogs
        />
      </React.Fragment>
    );
  },
);
